import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { LandMapEditor, polygonHectares, clearMapDraft } from "../../components/map/LandMap";
import { n } from "./shared";

export const ProjectFromLand = ({ land, onDone, onCancel }) => {
  const [f, setF] = useState({ projectCode: `${land.landCode}-P${String((land.projects?.length || 0) + 1).padStart(2, "0")}`, name: "", targetTrees: "", description: "", budgetTotal: "" });
  const [area, setArea] = useState([]);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const overlays = [{ points: land.boundary, color: "#22c55e", fill: 0.08, label: `Batas lahan ${land.landCode}` },
    ...(land.exclusions || []).map((e) => ({ points: e, color: "#dc2626", fill: 0.35, dashed: true, label: "Tidak dapat ditanami" })),
    ...(land.projects || []).filter((p) => p.area).map((p) => ({ points: p.area, color: "#ef4444", fill: 0.3, label: `Sudah dipakai: ${p.projectCode}` }))];
  const ha = polygonHectares(area);
  const suggested = Math.floor(ha * (land.planting?.treesPerHectare || 1600));
  const submit = async (e) => {
    e.preventDefault();
    if (area.length < 3) { toast.error("Gambar area kebun di peta (minimal 3 titik)"); return; }
    setBusy(true);
    try {
      await api.post(`/lands/${land.id}/projects`, { ...f, targetTrees: Number(f.targetTrees), budgetTotal: f.budgetTotal === "" ? null : Number(f.budgetTotal), area });
      clearMapDraft(`project-${land.id}`);
      toast.success("Rencana Kebun dibuat dan langsung tampil ke investor");
      onDone();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-4 rounded-xl border border-emerald-200 bg-emerald-50/40 p-4" data-testid="project-from-land-form">
      <p className="text-sm text-slate-700">Sisa kapasitas lahan: <b data-testid="form-remaining">{n(land.remainingTrees)} batang</b>. Area merah sudah dipakai kebun lain dan tidak boleh ditumpangi.</p>
      <div className="grid sm:grid-cols-3 gap-3">
        <div className="space-y-1"><Label>ID Kebun *</Label><Input required value={f.projectCode} onChange={set("projectCode")} data-testid="pfl-code" /></div>
        <div className="space-y-1"><Label>Nama Kebun *</Label><Input required value={f.name} onChange={set("name")} data-testid="pfl-name" /></div>
        <div className="space-y-1"><Label>Jumlah Batang (dasar investasi) *</Label><Input type="number" inputMode="numeric" required min="1" max={land.remainingTrees} value={f.targetTrees} onChange={set("targetTrees")} data-testid="pfl-trees" /></div>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="space-y-1"><Label>Keterangan</Label><Input value={f.description} onChange={set("description")} data-testid="pfl-description" /></div>
        <div className="space-y-1"><Label>Nilai kebutuhan investasi / RAB (Rp)</Label><Input type="number" inputMode="numeric" min="0" value={f.budgetTotal} onChange={set("budgetTotal")} data-testid="pfl-budget" /></div>
      </div>
      <LandMapEditor polygon={area} onPolygon={setArea} overlays={overlays} allowPoint={false} polygonLabel="Area Kebun" testid="project-map" draftKey={`project-${land.id}`} />
      {ha > 0 && <p className="text-xs text-slate-600">Area ±{ha.toLocaleString("id-ID", { maximumFractionDigits: 3 })} ha{suggested > 0 && ` · perkiraan muat ±${n(suggested)} batang`}</p>}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel} data-testid="pfl-cancel">Batal</Button>
        <Button disabled={busy} className="bg-emerald-800 hover:bg-emerald-900" data-testid="pfl-submit">{busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Buat Rencana Kebun</Button>
      </div>
    </form>
  );
};
